<template>
  <div
    ref="root"
    class="ag-slider"
  >
    <!-- Native scrolling, so touch, trackpads, wheels and keyboards all move it, and it snaps to each slide -->
    <div
      ref="track"
      class="ag-slider__track flex snap-x snap-mandatory gap-6 overflow-x-auto overscroll-x-contain px-8 md:px-10"
      :style="trackStyle"
    >
      <slot />
    </div>

    <div :class="$cn('px-8 md:px-10', ui?.inner)">
      <Container>
        <!-- Lines the first slide up with the page's content -->
        <div ref="guide" />

        <div
          v-if="hasOverflow"
          :class="$cn('-ml-3.5 mt-6 flex justify-end', ui?.nav)"
        >
          <Button
            v-show="!canSlideNext"
            icon="backward-fast"
            icon-prefix="fas"
            variant="link"
            :title="$t('back_to_start', 'Back to start')"
            :ui="{ base: 'text-paragraph hover:text-gray-700' }"
            @click="gotoFirstSlide"
          />
          <Button
            icon="long-arrow-left"
            icon-prefix="fas"
            variant="link"
            :title="$t('previous', 'Previous')"
            :disabled="!canSlidePrevious"
            :ui="{ base: 'text-paragraph hover:text-gray-700' }"
            @click="gotoPreviousSlide"
          />
          <Button
            icon="long-arrow-right"
            icon-prefix="fas"
            variant="link"
            :title="$t('next', 'Next')"
            :disabled="!canSlideNext"
            :ui="{ base: 'text-paragraph hover:text-gray-700' }"
            @click="gotoNextSlide"
          />
        </div>
      </Container>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useScroll } from '@vueuse/core'
import useBreakpoints from '~/composables/useBreakpoints'
import type { ClassValue } from 'clsx'
import type { Breakpoint } from '~/types/ui'

type SlidesPerView = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8

export interface SliderProps {
  breakpoints?: Partial<Record<Breakpoint, SlidesPerView>>
  perNavigation?: 1 | 'view'
  total: number
  ui?: Partial<{
    inner: ClassValue
    nav: ClassValue
  }>
}

const props = withDefaults(defineProps<SliderProps>(), {
  perNavigation: 1
})

const GAP = 24

const { breakpoints: breakpointsConfig, currentBreakpoint } = useBreakpoints()

// The slides set for the current breakpoint, or the nearest smaller one
const slidesPerView = computed<number>(() => {
  const minWidth = (breakpoint: Breakpoint): number => breakpointsConfig[breakpoint] ?? 0
  const matching = (Object.entries(props.breakpoints ?? {}) as [Breakpoint, SlidesPerView][])
    .filter(([breakpoint]) => minWidth(breakpoint) <= minWidth(currentBreakpoint.value))
    .sort(([a], [b]) => minWidth(b) - minWidth(a))

  return matching[0]?.[1] ?? 1
})

// Slides line up with the page's content, and run off to the edge of the screen
const root = useTemplateRef<HTMLElement>('root')
const track = useTemplateRef<HTMLElement>('track')
const guide = useTemplateRef<HTMLElement>('guide')

const { left: rootLeft, right: rootRight } = useElementBounding(root)
const { left: guideLeft, right: guideRight, width: guideWidth } = useElementBounding(guide)

const slideWidth = computed(() => (guideWidth.value - GAP * (slidesPerView.value - 1)) / slidesPerView.value)

const trackStyle = computed(() => {
  if (!guideWidth.value) return undefined

  const paddingLeft = `${guideLeft.value - rootLeft.value}px`

  return {
    '--slide-width': `${slideWidth.value}px`,
    'paddingLeft': paddingLeft,
    'paddingRight': `${rootRight.value - guideRight.value}px`,
    'scrollPaddingLeft': paddingLeft
  }
})

// Navigation
const { arrivedState, measure } = useScroll(track)

const hasOverflow = computed(() => props.total > slidesPerView.value)
const canSlidePrevious = computed(() => !arrivedState.left)
const canSlideNext = computed(() => !arrivedState.right)

const perNavigation = computed(() => (props.perNavigation === 'view' ? slidesPerView.value : props.perNavigation))

const scrollBySlides = (slides: number) => {
  track.value?.scrollBy({ left: slides * (slideWidth.value + GAP), behavior: 'smooth' })
}

const gotoNextSlide = () => scrollBySlides(perNavigation.value)
const gotoPreviousSlide = () => scrollBySlides(-perNavigation.value)
const gotoFirstSlide = () => track.value?.scrollTo({ left: 0, behavior: 'smooth' })

// Arrows reflect where it starts, before anything has scrolled
watch(slideWidth, () => nextTick(measure))
</script>

<style>
.ag-slider__track {
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: none;
    width: var(--slide-width, 85%);
    scroll-snap-align: start;
  }
}
</style>
